import React, { useEffect, useRef } from "react";
import { CheckCircle2, CircleDashed, Loader2, MinusCircle, X, XCircle } from "lucide-react";
import { getPromoteAll } from "../../lib/uploadsApi";
import { num, when } from "../pipeline/Layer";

const ICON = {
  waiting: [CircleDashed, "text-muted", "Waiting"], promoting: [Loader2, "text-primary animate-spin", "Promoting…"], done: [CheckCircle2, "text-success", "Promoted"],
  failed: [XCircle, "text-critical", "Failed"], skipped: [MinusCircle, "text-warning", "Skipped"], stopped: [MinusCircle, "text-muted", "Stopped by a restart"],
};
const SEEN = "rd-batch-dismissed";

function FileLine({ f }) {
  const [Icon, cls, label] = ICON[f.state] || ICON.waiting;
  const extra = f.state === "done" ? `${num(f.rows_added)} rows added${f.overlap_skipped ? ` · ${num(f.overlap_skipped)} already in Silver` : ""}` : f.reason;
  return (
    <li className="flex items-start gap-2 text-[12px]" data-testid={`rd-batch-file-${f.id}`}>
      <Icon size={13} className={`mt-0.5 shrink-0 ${cls}`} />
      <span className="font-mono-tabular">{f.filename}</span><span className="text-muted">{label}{extra ? ` — ${extra}` : ""}</span>
    </li>
  );
}

const outcome = (b) => b.state === "stopped" ? "Stopped by a server restart — unfinished files stay in Bronze"
  : b.state === "error" ? `Stopped: ${b.error}` : `${b.counts?.done ?? 0} promoted · ${b.counts?.failed ?? 0} failed · ${b.counts?.skipped ?? 0} skipped · ${when(b.ended_at)}`;

// The latest Promote all: live per-file progress while it runs (also one started elsewhere), then the outcome until dismissed.
export default function BatchProgress({ b, setB, onStarted, onFinished }) {
  const was = useRef(b?.state);
  useEffect(() => {
    const t = setTimeout(() => getPromoteAll().then(setB).catch(() => {}), b?.state === "running" ? 2000 : 20000);
    return () => clearTimeout(t);
  }, [b, setB]);
  useEffect(() => {
    if (was.current && was.current !== "running" && b?.state === "running") onStarted();
    if (was.current === "running" && b?.state !== "running") onFinished();
    was.current = b?.state;
  }, [b, onStarted, onFinished]);
  if (!b || b.state === "none" || localStorage.getItem(SEEN) === b.id) return null;
  if (b.state !== "running" && b.ended_at && Date.now() - new Date(b.ended_at).getTime() > 15 * 60 * 1000) return null;  // older outcomes live in the data history
  const dismiss = () => { localStorage.setItem(SEEN, b.id); setB({ ...b }); };
  return (
    <div className="border border-border rounded-sm p-3 space-y-2 bg-surface-2/40" data-testid="rd-batch">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <span className="font-semibold">Promote all · {b.label}</span>
        <span className="text-muted font-mono-tabular" data-testid="rd-batch-state">{b.state === "running" ? `${num(b.done)} of ${num(b.total)} done${b.current ? ` — ${b.current}` : ""}` : outcome(b)}</span>
        {b.state !== "running" && <button data-testid="rd-batch-dismiss" onClick={dismiss} className="ml-auto w-6 h-6 inline-flex items-center justify-center rounded-sm hover:bg-surface-2"><X size={13} /></button>}
      </div>
      {b.state === "running" && <div className="h-1.5 bg-surface-2 rounded-full max-w-md"><div className="h-full bg-primary rounded-full transition-[width] duration-500" style={{ width: `${b.total ? Math.round((b.done / b.total) * 100) : 0}%` }} /></div>}
      <ul className="space-y-1">{b.files.map(f => <FileLine key={f.id} f={f} />)}</ul>
    </div>
  );
}
